// Api Models

import * as moment from 'moment';
import {
  ApiCreateBankAccountPaymentMethodRequest,
  ApiCreateCreditCardPaymentMethodRequest,
  ApiInternalEditPaymentMethod,
  BankAccountType,
  CreditCardProcessorName,
  PaymentMethodType,
} from './payment-method.model';
import { CloseMethod } from './notifications.model';

export enum TestPatientPaymentMethodOption {
  DoNothing,
  OneCreditCard,
  NoPaymentMethods,
  OneExpiredCreditCard,
  OneValidAndOneExpiredCreditCard,
  OneCreditCardAndOneBankAccount,
}

export enum TestPatientNotificationOption {
  DoNothing,
  NoNotifications,
  DisplayNotification,
  ActionNotificationWithOneButton,
  ActionNotificationWithTwoButtons,
  ExpirationNotification,
  Cgm90EnrollmentNotification,
  TwoActionNotifications,
}

export enum TestPatientCopayExclusionOption {
  DoNothing,
  AddToExclusionList,
  RemoveFromExclusionList,
}

export enum TestPatientDeductibleOverrideOption {
  DoNothing,
  PrimaryAllowedSecondaryAllowed,
  PrimaryAllowedSecondaryDisallowed,
  PrimaryDisallowedSecondaryAllowed,
  PrimaryDisallowedSecondaryDisallowed,
}

export interface ApiCreateTestPatientNotificationRequest {
  priority: number;
  closeMethod: CloseMethod;
  externalText?: string;
  externalButtonLabel1?: string;
  externalButtonLabel2?: string;
  externalCustomActionId?: string;
  expiresOn: string; // Must be an ISO String (i.e. moment().toISOString())
  notificationType: string;
  patientId: number;
  status: string;
}

export interface ApiSetTestPatientCopayExclusionRequest {
  excludeCopay: boolean;
}

export interface ApiSetTestPatientDeductibleOverridesRequest {
  canOverridePrimaryDeductible: boolean;
  canOverrideSecondaryDeductible: boolean;
}

interface TestPatientsTestData {
  expireCreditCardRequest: ApiInternalEditPaymentMethod;
  createFirstCreditCardRequest: ApiCreateCreditCardPaymentMethodRequest;
  createSecondCreditCardRequest: ApiCreateCreditCardPaymentMethodRequest;
  createBankAccountRequest: ApiCreateBankAccountPaymentMethodRequest;
  createDisplayNotificationRequest: (
    patientId: number,
    priorityIncrement?: number
  ) => ApiCreateTestPatientNotificationRequest;
  createOneButtonActionNotificationRequest: (
    patientId: number,
    priorityIncrement?: number
  ) => ApiCreateTestPatientNotificationRequest;
  createTwoButtonActionNotificationRequest: (
    patientId: number,
    priorityIncrement?: number
  ) => ApiCreateTestPatientNotificationRequest;
  createExpirationNotificationRequest: (
    patientId: number,
    priorityIncrement?: number
  ) => ApiCreateTestPatientNotificationRequest;
  createCgm90EnrollmentNotificationRequest: (
    patientId: number,
    cgm90EnrollmentActionId: string,
    priorityIncrement?: number
  ) => ApiCreateTestPatientNotificationRequest;
  createTestPatientCopayExclusionRequest: (excludeCopay: boolean) => ApiSetTestPatientCopayExclusionRequest;
  createTestPatientDeductibleOverridesRequest: (
    primaryOverride: boolean,
    secondaryOverride: boolean
  ) => ApiSetTestPatientDeductibleOverridesRequest;
}

export const testPatientsTestData: TestPatientsTestData = {
  expireCreditCardRequest: {
    method: { expirationYear: '2020' },
  },
  createFirstCreditCardRequest: {
    default: true,
    firstName: 'Test',
    lastName: 'Patient',
    currency: 'USD',
    address: {
      address1: '123 Test Lane',
      city: 'Testville',
      state: 'TX',
      zipCode: '12345',
      country: 'US',
    },
    method: {
      type: PaymentMethodType.CreditCard,
      number: '6011111111111117',
      cvv: '623',
      expirationMonth: '01',
      expirationYear: moment().add(2, 'years').format('YYYY'),
      processor: CreditCardProcessorName.Discover,
      healthAccount: true,
    },
  },
  createSecondCreditCardRequest: {
    default: false,
    firstName: 'Test',
    lastName: 'Patient',
    currency: 'USD',
    address: {
      address1: '123 Test Lane',
      city: 'Testville',
      state: 'TX',
      zipCode: '12345',
      country: 'US',
    },
    method: {
      type: PaymentMethodType.CreditCard,
      number: '4111111111111111',
      cvv: '123',
      expirationMonth: '03',
      expirationYear: moment().add(2, 'years').format('YYYY'),
      processor: CreditCardProcessorName.Visa,
      healthAccount: false,
    },
  },
  createBankAccountRequest: {
    default: true,
    firstName: 'Test',
    lastName: 'Patient',
    currency: 'USD',
    address: {
      address1: '123 Test Lane',
      city: 'Testville',
      state: 'TX',
      zipCode: '12345',
      country: 'US',
    },
    method: {
      type: PaymentMethodType.BankAccount,
      accountNumber: '12345678',
      routingNumber: '112200439',
      accountType: BankAccountType.Checking,
    },
  },
  createDisplayNotificationRequest: (patientId: number, priorityIncrement: number = 0) => {
    const ts = moment().add(20, 'minutes').toISOString();
    const id = (moment().unix() % 10000) + priorityIncrement;
    return {
      patientId,
      priority: id,
      closeMethod: CloseMethod.Display,
      externalText:
        'To avoid delays to your order, please remember to update your insurance information if you received a new insurance card.',
      expiresOn: ts,
      notificationType: `TEST${id}`,
      status: 'OPEN',
    };
  },
  createOneButtonActionNotificationRequest: (patientId: number, priorityIncrement: number = 0) => {
    const ts = moment().add(20, 'minutes').toISOString();
    const id = (moment().unix() % 10000) + priorityIncrement;
    return {
      patientId,
      priority: id,
      closeMethod: CloseMethod.Action,
      externalText:
        "CCS Medical shipped you a 90-day supply of CGM supplies. Based on your prescribed usages, you should have enough supplies to continue using your CGM and for your next monthly claim to be billed. Please press confirm below to indicate you're using your CGM and authorize CCS Medical to bill your next monthly claim.",
      externalButtonLabel1: 'Confirm',
      expiresOn: ts,
      notificationType: `TEST${id}`,
      status: 'OPEN',
    };
  },
  createTwoButtonActionNotificationRequest: (patientId: number, priorityIncrement: number = 0) => {
    const ts = moment().add(20, 'minutes').toISOString();
    const id = (moment().unix() % 10000) + priorityIncrement;
    return {
      patientId,
      priority: id,
      closeMethod: CloseMethod.Action,
      externalText:
        'Did you know that we offer Ostomy and Urology supplies? Are you interested in hearing about these products?',
      externalButtonLabel1: 'Yes, please contact me!',
      externalButtonLabel2: 'No thanks',
      expiresOn: ts,
      notificationType: `TEST${id}`,
      status: 'OPEN',
    };
  },
  createExpirationNotificationRequest: (patientId: number, priorityIncrement: number = 0) => {
    const ts = moment().add(20, 'minutes').toISOString();
    const id = (moment().unix() % 10000) + priorityIncrement;
    return {
      patientId,
      priority: id,
      closeMethod: CloseMethod.Expiration,
      externalText:
        'To avoid delays to your order, please remember to update your insurance information if you received a new insurance card.',
      expiresOn: ts,
      notificationType: `TEST${id}`,
      status: 'OPEN',
    };
  },
  createCgm90EnrollmentNotificationRequest: (
    patientId: number,
    cgm90EnrollmentActionId: string,
    priorityIncrement: number = 0
  ) => {
    const ts = moment().add(20, 'minutes').toISOString();
    const id = (moment().unix() % 10000) + priorityIncrement;
    return {
      patientId,
      priority: id,
      closeMethod: CloseMethod.Action,
      externalCustomActionId: cgm90EnrollmentActionId,
      expiresOn: ts,
      notificationType: `TEST${id}`,
      status: 'OPEN',
    };
  },
  createTestPatientCopayExclusionRequest: (excludeCopay: boolean): ApiSetTestPatientCopayExclusionRequest => ({
    excludeCopay,
  }),
  createTestPatientDeductibleOverridesRequest: (
    primaryOverride: boolean,
    secondaryOverride: boolean
  ): ApiSetTestPatientDeductibleOverridesRequest => ({
    canOverridePrimaryDeductible: primaryOverride,
    canOverrideSecondaryDeductible: secondaryOverride,
  }),
};

export interface ApiTestPatientRequest {
  minNumOfInsurances?: number;
  maxNumOfInsurances?: number;
  insuranceTypeIds?: number[];
  minNumOfProviders?: number;
  maxNumOfProviders?: number;
  minNumOfAddresses?: number;
  maxNumOfAddresses?: number;
  minNumOfPastOrders?: number;
  maxNumOfPastOrders?: number;
  minNumOfReorders?: number;
  maxNumOfReorders?: number;
  shipmentTypeIds?: number[];
  existingShipmentTypeIds?: number[];
  minNumOfReorderItems?: number;
  maxNumOfReorderItems?: number;
  minOutstandingBalance?: number;
  maxOutstandingBalance?: number;
  limit?: number;
  targetShipDateDaysBack?: number;
  targetShipDateDaysForward?: number;
  hasPaymentPlan?: boolean;
  hasRentalSchedule?: boolean;
  hasDaysOnHandRequired?: boolean;
  hasTestingFrequencyRequired?: boolean;
  hasPumpSiteChangeFrequencyRequired?: boolean;
  hasLastProviderVisit?: boolean;
  hasMismatchingTargetShipDates?: boolean;
  hasNoChargeItems?: boolean;
  hasMissingDocumentation?: boolean;
  hasSetsOrReservoirs?: boolean;
  hasSensors?: boolean;
  hasTestingSupplies?: boolean;
  useOrderStatus?: boolean;
  isPortalUser?: boolean;
  isNewLeafPatient?: boolean;
}

export interface ApiRegistrationTestPatientContact {
  patientId: number;
  primaryEmail: string;
  // Fixed values -- don't care for testing purposes
  emailConsentPatientBusiness: true;
  emailConsentMarketing: true;
  primaryPhone: '1112223333';
  primaryPhoneSubType: 'm';
  phoneConsentPatientBusiness: true;
  phoneConsentMarketing: true;
}

export interface TestPatientReorderCard {
  orderId: number;
  shipmentTypeId: number;
  shipmentTypeDesc: string;
  targetShipDate: string;
  dateOfService: string;
  numOfReorderItems: number;
  numOfDaysOnHandRequiredItems: number;
  numOfTestingFrequencyRequiredItems: number;
  numOfPumpSiteChangeFrequencyRequiredItems: number;
  lastProviderVisitRequired: boolean;
  hasMismatchingTargetShipDates: boolean;
  numOfNoChargeItems: number;
  hasMissingDocumentation: boolean;
  numOfSetOrReservoirItems: number;
  numOfSensorItems: number;
  numOfTestingSupplyItems: number;
}

export interface TestPatient {
  id: number;
  firstName: string;
  lastName: string;
  balance: number;
  insuranceTypeId: number;
  insuranceTypeName: string;
  primaryReportGroup: string;
  numOfProviders: number;
  numOfReorders: number;
  numOfInsurances: number;
  numOfAddresses: number;
  numOfPastOrders: number;
  hasPaymentPlan: boolean;
  hasRentalSchedule: boolean;
  reorderCards: TestPatientReorderCard[];
}

export interface RegistrationTestPatient {
  patientId: number;
  firstName: string;
  lastName: string;
  insuranceNumber: string;
  dob: string; // yyyy-mm-dd
  zipCode: string;
}

export const buildUpdateRegistrationTestPatientContactsRequest = (
  patientId: number,
  primaryEmail: string
): ApiRegistrationTestPatientContact => {
  return {
    patientId,
    primaryEmail,
    // Fixed values -- don't care for testing purposes
    emailConsentPatientBusiness: true,
    emailConsentMarketing: true,
    primaryPhone: '1112223333',
    primaryPhoneSubType: 'm',
    phoneConsentPatientBusiness: true,
    phoneConsentMarketing: true,
  };
};
